// computerUseTool.js — agent-computer-use E3. Exposes the gated OS backend
// (electron/computerUse.cjs) to the model via the agentLoop `extraTools` seam,
// exactly like harvestTools. The tool executes in the renderer and calls the
// preload bridge `window.apolo.computerUse.*`; the Electron main process runs the
// authoritative permission gate (default-DENY, per-session allowlist, tier +
// frontmost check), so the renderer is never trusted with OS access.
//
// GATING (safe by default): the tool is built ONLY when BOTH
//   (a) the bridge exists (packaged Electron app), and
//   (b) the user opted in (localStorage apolo:computer-use:enabled === '1').
// Off ⇒ resolveComputerUseTools() returns [] ⇒ the model's tool set is unchanged.
// `mutating: true` opts into the loop's approval gate and keeps read-only workers
// from ever receiving it.

const ENABLED_KEY = 'apolo:computer-use:enabled';

export const COMPUTER_USE_DEF = {
  type: 'function',
  function: {
    name: 'computer_use',
    description:
      'Controla o computador (macOS) por visão + eventos, como o "computer use" do Claude. '
      + 'FLUXO: 1) action="grant" {apps:["Notes","Finder"]} para pedir acesso aos apps que vai usar; '
      + '2) action="screenshot" para ver a tela; 3) aja: click {x,y} · type {text} · key {combo, ex "cmd+a"} · applescript {script}. '
      + 'GATES por app: navegadores = só leitura (dirija a web por web_act, não por clique de pixel); terminais/IDEs = só clique (nunca digitar = sem exec arbitrário); demais apps = completo. '
      + 'Uma ação só é permitida se o app está EM FOCO e foi concedido. Tire screenshot antes de agir por pixel.',
    parameters: {
      type: 'object',
      properties: {
        action: { type: 'string', enum: ['grant', 'frontmost', 'screenshot', 'click', 'type', 'key', 'applescript'] },
        apps: { type: 'array', items: { type: 'string' }, description: 'para grant: nomes dos apps' },
        x: { type: 'number' }, y: { type: 'number' },
        text: { type: 'string', description: 'para type' },
        combo: { type: 'string', description: 'para key, ex "cmd+shift+4"' },
        script: { type: 'string', description: 'para applescript' },
      },
      required: ['action'],
    },
  },
};

/** Compact, model-facing STRING for a computer_use result. Pure; never throws. */
export function formatCuResult(action, data) {
  const d = data && typeof data === 'object' ? data : {};
  if (action === 'grant') return d.ok ? `Acesso concedido a: ${(d.granted || []).join(', ') || '(nenhum)'}.` : `Erro grant: ${d.reason || 'falhou'}.`;
  if (action === 'frontmost') return d.ok ? `App em foco: ${d.app || '(desconhecido)'}.` : `Erro frontmost: ${d.reason || 'falhou'}.`;
  if (!d.ok) return `Bloqueado/erro (${action}): ${d.reason || 'sem motivo'}.`;
  if (action === 'screenshot') return `Screenshot salvo em ${d.path} (a imagem foi capturada; peça inspeção por AppleScript/coordenadas se precisar de detalhe).`;
  if (action === 'applescript') return `AppleScript ok. Saída: ${String(d.output ?? '').slice(0, 500) || '(vazia)'}`;
  return `Ação "${action}" ok${d.app ? ` (app: ${d.app})` : ''}${d.tier ? `, tier ${d.tier}` : ''}.`;
}

const bridge = () => {
  const w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});
  return w?.apolo?.computerUse || null;
};

/** Is the computer-use tool available (bridge present) AND opted-in? */
export function isComputerUseEnabled() {
  if (!bridge()) return false;
  try { return window.localStorage.getItem(ENABLED_KEY) === '1'; } catch { return false; }
}

export function setComputerUseEnabled(on) {
  try { window.localStorage.setItem(ENABLED_KEY, on ? '1' : '0'); } catch { /* no storage */ }
}

/** Dispatch one action to the preload bridge; returns the raw {ok,...} object. */
async function callBridge(action, args) {
  const cu = bridge();
  if (!cu) return { ok: false, reason: 'computer-use indisponível (fora do app empacotado)' };
  switch (action) {
    case 'grant': return cu.grant(Array.isArray(args.apps) ? args.apps : []);
    case 'frontmost': return cu.frontmost();
    case 'screenshot': return cu.screenshot();
    case 'click': return cu.click({ x: args.x, y: args.y });
    case 'type': return cu.type({ text: args.text });
    case 'key': return cu.key({ combo: args.combo });
    case 'applescript': return cu.applescript({ script: args.script });
    default: return { ok: false, reason: `ação desconhecida: ${action}` };
  }
}

/** Build the {def, execute, mutating} tool for the extraTools seam. */
export function makeComputerUseTool() {
  return {
    def: COMPUTER_USE_DEF,
    mutating: true,
    execute: async (args = {}, ctx = {}) => {
      const action = String(args?.action || '');
      if (!COMPUTER_USE_DEF.function.parameters.properties.action.enum.includes(action)) {
        return `Erro computer_use: action inválida "${action}".`;
      }
      try {
        const data = await callBridge(action, args);
        // Feed the screenshot to a vision model via the loop's image hook, so the
        // agent SEES the screen before clicking.
        if (action === 'screenshot' && data?.ok && data.image && typeof ctx.attachImages === 'function') {
          ctx.attachImages([data.image]);
        }
        return formatCuResult(action, data);
      } catch (e) {
        return `Erro computer_use (${action}): ${e?.message || e}`;
      }
    },
  };
}

/** Gated resolver for SessionWorkspace: [] unless the bridge exists AND the user opted in. */
export function resolveComputerUseTools() {
  return isComputerUseEnabled() ? [makeComputerUseTool()] : [];
}
